/**
 * The plan's own facts, under its name: the two status pickers with the NOS Deal Stage,
 * Chance of Close and NOS Demand beside them, the plan window, and the
 * terms and people a reader checks before reading a figure.
 *
 * It replaced a single dot-separated monospace line. Every fact was there, but the start
 * and end months sat mid-sentence in the same weight as "Total" and "CSM", so planners
 * hunted for the one thing they most needed — when the work runs. The window now leads,
 * labelled Start and End, and every other fact carries its own label.
 *
 * The people on the plan are ONE "Team" fact — `<Author> · Author · <CP> · CP · <CSM> ·
 * CSM` — not four label/value pairs ("Say it once", docs/design-system.md). Empty CRM
 * roles are left off the line; the popover behind it still lists them as Not assigned,
 * because that is where a reader learns they are unset.
 */

import { ArrowRight, CalendarRange, Pencil } from "lucide-react";
import { useState } from "react";
import { PlanAccountTeamButton } from "@/components/plans/PlanAccountTeam";
import { PlanDealStageChips } from "@/components/plans/PlanDealStageBar";
import { PlanNosDemandChip } from "@/components/plans/PlanNosDemandChip";
import { PlanRateCardDrawer } from "@/components/plans/PlanRateCardDrawer";
import { PlanResourcingControl } from "@/components/plans/PlanResourcingControl";
import { PlanStatusMenu } from "@/components/plans/PlanStatusControl";
import { StaffingNeedBadge } from "@/components/plans/StaffingNeedBadge";
import { usePlanRateCard } from "@/hooks/use-plan-rate-card";
import type { PlanLineView, ResourcePlanDetailView } from "@/schemas/resource-plans";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** `2026-10` → `Oct 2026`. */
function longMonth(month: string): string {
  const [year, m] = month.split("-");
  return `${MONTH_NAMES[Number(m) - 1] ?? m} ${year ?? ""}`;
}

/** Inclusive month count between two `YYYY-MM` months. */
function monthSpan(start: string, end: string): number {
  const [sy = 0, sm = 0] = start.split("-").map(Number);
  const [ey = 0, em = 0] = end.split("-").map(Number);
  return (ey - sy) * 12 + (em - sm) + 1;
}

function customizedRateCount(lines: readonly PlanLineView[]): number {
  return lines.filter((l) => l.rateSource === "LineOverride" || l.rateSource === "ClientDictated")
    .length;
}

function Fact(props: { label: string; title?: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5" title={props.title}>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{props.label}</dt>
      <dd className="truncate text-sm font-medium text-foreground">{props.children}</dd>
    </div>
  );
}

function PeriodEdge(props: { label: string; month: string | null }) {
  return (
    <span className="flex flex-col">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{props.label}</span>
      <span className="text-base font-semibold text-foreground tabular-nums">
        {props.month === null ? "Open-ended" : longMonth(props.month)}
      </span>
    </span>
  );
}

export function PlanHeaderFacts({
  plan,
  onChangeOwner,
  onChangeDates,
}: {
  plan: ResourcePlanDetailView;
  onChangeOwner: () => void;
  /** RP-28: opens "Change dates". Absent for readers who cannot edit the plan. */
  onChangeDates?: () => void;
}) {
  const customized = customizedRateCount(plan.lines);
  const endMonth = plan.endMonth ?? null;
  // RP-6.6: the card the plan ACTUALLY prices from — the Account's only when a negotiated
  // rate applies. Until it loads, the basis alone is the one thing known to be true.
  const rateCard = usePlanRateCard(plan.id);
  const [rateCardOpen, setRateCardOpen] = useState(false);

  return (
    <section aria-label="Plan details" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* The badge IS the status picker — Sales beside the RM's Resourcing status. */}
        <PlanStatusMenu plan={plan} />
        <PlanResourcingControl plan={plan} />
        {/* RP-35: where the NOS Deal stands, and the plan's own Chance of Close. */}
        <PlanDealStageChips plan={plan} />
        {/* The NOS Demand this plan's guidance belongs to: closed months lock, open months
            post back (src/components/plans/PlanNosDemandChip.tsx). */}
        <PlanNosDemandChip plan={plan} />
        {/* How firm the need is: the Solution's open Staffing Request priority, mirrored
            live and read-only (RP-18.4). */}
        {plan.staffingNeed != null && (
          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            Staffing need:
            <StaffingNeedBadge need={plan.staffingNeed} showCount />
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
        <div
          className="flex items-center gap-3 rounded-md bg-accent px-3 py-1.5"
          title="The plan's window — the first and last month it prices."
        >
          <CalendarRange className="h-4 w-4 shrink-0 text-primary" />
          <PeriodEdge label="Start" month={plan.startMonth} />
          <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          <PeriodEdge label="End" month={endMonth} />
          {endMonth !== null && (
            <span className="self-end pb-0.5 text-xs text-muted-foreground">
              {monthSpan(plan.startMonth, endMonth)} months
            </span>
          )}
          {onChangeDates !== undefined && (
            <button
              type="button"
              onClick={onChangeDates}
              className="inline-flex items-center gap-1 self-end pb-0.5 text-xs font-medium text-primary hover:underline"
            >
              <Pencil className="h-3 w-3" aria-hidden />
              Change dates
            </button>
          )}
        </div>

        <dl className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <Fact label="Pricing">
            {plan.pricingModel === "FixedPrice" ? "Fixed Price" : "T&M"}
            <span className="font-normal text-muted-foreground"> · {plan.budgetCadence}</span>
          </Fact>
          <Fact
            label="Rates"
            title="Resolved at creation and frozen into this plan. A later Rate Card change never silently reprices it. Click to see the card."
          >
            <button
              type="button"
              onClick={() => setRateCardOpen(true)}
              className="font-medium text-foreground underline decoration-dotted underline-offset-4 transition-colors hover:text-primary"
            >
              {rateCard.data?.headline ?? plan.rateBasisLabel}
            </button>
            {customized > 0 && (
              <span className="ml-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {customized} customized
              </span>
            )}
          </Fact>
          <Fact
            label="Team"
            title="The people on this plan. Click for emails and the full account team."
          >
            <span className="inline-flex min-w-0 items-center gap-1">
              <PlanAccountTeamButton planId={plan.id}>
                <span className="truncate">
                  {plan.ownerName ?? <span className="text-muted-foreground">Unassigned</span>}
                  <span className="font-normal text-muted-foreground"> · Author</span>
                  {plan.crmClientPartnerName !== null && (
                    <>
                      <span className="font-normal text-muted-foreground"> · </span>
                      {plan.crmClientPartnerName}
                      <span className="font-normal text-muted-foreground"> · CP</span>
                    </>
                  )}
                  {plan.crmCsmName !== null && (
                    <>
                      <span className="font-normal text-muted-foreground"> · </span>
                      {plan.crmCsmName}
                      <span className="font-normal text-muted-foreground"> · CSM</span>
                    </>
                  )}
                </span>
              </PlanAccountTeamButton>
              {plan.canChangeOwner === true && (
                <button
                  type="button"
                  onClick={onChangeOwner}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                  aria-label="Change plan Author"
                  title="Change who owns this plan"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              )}
            </span>
          </Fact>
        </dl>
      </div>
      {rateCardOpen && (
        <PlanRateCardDrawer planId={plan.id} onClose={() => setRateCardOpen(false)} />
      )}
    </section>
  );
}
